/**
 * Chi-squared goodness-of-fit against the uniform law, without scipy or any
 * dependency. Port of the regularised upper incomplete gamma function used by
 * matrice_v2.py.
 *
 * This is a DIAGNOSTIC, not a signal. It exists to detect a broken machine, and
 * it is the only legitimate reason to look at draw history at all. A high count
 * for some number is not a reason to play it: see docs and the Method screen.
 */

function gammaincUpperReg(s: number, x: number): number {
  if (x < s + 1) {
    let term = 1 / s;
    let total = term;
    let i = 0;
    while (Math.abs(term) > 1e-15 * Math.abs(total) && i < 10000) {
      i++;
      term *= x / (s + i);
      total += term;
    }
    return 1 - total * Math.exp(-x + s * Math.log(x) - logGamma(s));
  }
  let b = x + 1 - s;
  let c = 1e300;
  let d = 1 / b;
  let h = d;
  for (let i = 1; i < 10000; i++) {
    const an = -i * (i - s);
    b += 2;
    d = an * d + b;
    if (Math.abs(d) < 1e-300) d = 1e-300;
    c = b + an / c;
    if (Math.abs(c) < 1e-300) c = 1e-300;
    d = 1 / d;
    const de = d * c;
    h *= de;
    if (Math.abs(de - 1) < 1e-15) break;
  }
  return h * Math.exp(-x + s * Math.log(x) - logGamma(s));
}

/** Lanczos approximation of log(Gamma(z)), g = 7, n = 9. */
const LANCZOS = [
  0.99999999999980993, 676.5203681218851, -1259.1392167224028, 771.32342877765313,
  -176.61502916214059, 12.507343278686905, -0.13857109526572012, 9.9843695780195716e-6,
  1.5056327351493116e-7,
];

export function logGamma(z: number): number {
  if (z < 0.5) {
    return Math.log(Math.PI / Math.sin(Math.PI * z)) - logGamma(1 - z);
  }
  const zz = z - 1;
  let x = LANCZOS[0] as number;
  for (let i = 1; i < LANCZOS.length; i++) x += (LANCZOS[i] as number) / (zz + i);
  const t = zz + LANCZOS.length - 1.5;
  return 0.5 * Math.log(2 * Math.PI) + (zz + 0.5) * Math.log(t) - t + Math.log(x);
}

export function chi2PValue(chi2: number, df: number): number {
  if (df <= 0) return Number.NaN;
  if (chi2 <= 0) return 1;
  return gammaincUpperReg(df / 2, chi2 / 2);
}

export interface UniformityResult {
  /** Number of draws in the window. */
  draws: number;
  chi2: number;
  df: number;
  p: number;
  /** Observed count per number, index 0 = number 1. */
  counts: number[];
  /** Expected count per number under the uniform law. */
  expected: number;
  /** Largest standardised deviation |z| observed. */
  maxAbsZ: number;
  /** Two-sided Bonferroni threshold on |z| for this many numbers, alpha = 0.001. */
  bonferroniZ: number;
  /** True when the sample is too small for the test to say much. */
  underpowered: boolean;
}

/**
 * @param picks list of draws, each an array of numbers in 1..nmax
 */
export function uniformity(
  picks: readonly (readonly number[])[],
  nmax: number,
  perDraw: number,
): UniformityResult {
  const counts = new Array<number>(nmax).fill(0);
  for (const draw of picks) {
    for (const x of draw) {
      if (x >= 1 && x <= nmax) counts[x - 1] = (counts[x - 1] as number) + 1;
    }
  }
  const draws = picks.length;
  const expected = (draws * perDraw) / nmax;
  let chi2 = 0;
  let maxAbsZ = 0;
  if (expected > 0) {
    // sd of a count under the uniform model, binomial(draws*perDraw, 1/nmax)
    const sd = Math.sqrt(draws * perDraw * (1 / nmax) * (1 - 1 / nmax));
    for (const o of counts) {
      chi2 += ((o - expected) * (o - expected)) / expected;
      const z = Math.abs((o - expected) / sd);
      if (z > maxAbsZ) maxAbsZ = z;
    }
  }
  const df = nmax - 1;
  return {
    draws,
    chi2,
    df,
    p: expected > 0 ? chi2PValue(chi2, df) : Number.NaN,
    counts,
    expected,
    maxAbsZ,
    bonferroniZ: bonferroniZ(nmax),
    underpowered: draws < 100,
  };
}

/** |z| threshold such that P(any of `m` standard normals exceeds it) = alpha. */
export function bonferroniZ(m: number, alpha = 0.001): number {
  return normalQuantile(1 - alpha / (2 * m));
}

/** Acklam's inverse normal CDF; accurate to ~1e-9, plenty for a threshold. */
export function normalQuantile(p: number): number {
  if (p <= 0 || p >= 1) return Number.NaN;
  const a = [
    -3.969683028665376e1, 2.209460984245205e2, -2.759285104469687e2, 1.38357751867269e2,
    -3.066479806614716e1, 2.506628277459239,
  ];
  const b = [
    -5.447609879822406e1, 1.615858368580409e2, -1.556989798598866e2, 6.680131188771972e1,
    -1.328068155288572e1,
  ];
  const c = [
    -7.784894002430293e-3, -3.223964580411365e-1, -2.400758277161838, -2.549732539343734,
    4.374664141464968, 2.938163982698783,
  ];
  const d = [
    7.784695709041462e-3, 3.224671290700398e-1, 2.445134137142996, 3.754408661907416,
  ];
  const pLow = 0.02425;
  const pHigh = 1 - pLow;

  if (p < pLow) {
    const q = Math.sqrt(-2 * Math.log(p));
    return horner(c, q) / (horner(d, q) * q + 1);
  }
  if (p > pHigh) return -normalQuantile(1 - p);

  const q = p - 0.5;
  const r = q * q;
  return (horner(a, r) * q) / (horner(b, r) * r + 1);
}

/** Evaluate a polynomial given highest-degree coefficient first. */
function horner(coefficients: readonly number[], x: number): number {
  let acc = 0;
  for (const k of coefficients) acc = acc * x + k;
  return acc;
}
